import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { queryKeys } from './queryKeys';
import { documentService } from '@/services/documentService';
import { useQueryClient } from '@tanstack/react-query';
import { APIError } from '@/utils/api-client';
import {
  EntityType,
  type FundDocumentContextType,
  type DocumentCollection,
  type CreateCollectionRequest,
  type UploadDocumentRequest,
  type DocumentUploadResponse,
} from '@/types/document';

// Collection hooks
export const useCollections = (filters?: { parent_id?: string }) => {
  return useApiQuery(
    queryKeys.documents.list(filters),
    () => documentService.getCollections(filters),
    {
      enabled: true, // Always enabled - the service handles the filters correctly
    }
  );
};

export const useCollectionContents = (collectionId: string) => {
  const isValidId =
    !!collectionId &&
    collectionId !== 'root' &&
    collectionId !== 'null' &&
    collectionId !== 'undefined';

  return useApiQuery(
    [...queryKeys.documents.folders(), collectionId, 'contents'],
    () => documentService.getCollectionContents(collectionId),
    {
      enabled: isValidId,
    }
  );
};

// Document mutations
export const useCreateCollection = () => {
  return useApiMutation<DocumentCollection, APIError, CreateCollectionRequest>(
    (data) => documentService.createCollection(data),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Folder created successfully',
    }
  );
};

export const useDeleteCollection = () => {
  return useApiMutation<void, APIError, string>(
    (id) => documentService.deleteCollection(id),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Folder deleted successfully',
    }
  );
};

export const useUploadDocument = () => {
  return useApiMutation<
    DocumentUploadResponse,
    APIError,
    UploadDocumentRequest
  >((data) => documentService.uploadDocument(data), {
    invalidateKeys: [queryKeys.documents.all],
    successMessage: 'Document uploaded successfully',
  });
};

/**
 * Error raised when /upload/bulk reports per-file failures. That endpoint answers
 * HTTP 200 with a per-file result list, so the failures never reach the generic
 * error toast unless we throw here. `results` carries the full per-file list so
 * callers can still render each file's status.
 */
export interface BulkUploadError extends APIError {
  results?: DocumentUploadResponse[];
}

const buildBulkUploadErrorMessage = (
  results: DocumentUploadResponse[]
): string => {
  const failures = results.filter((result) => !result.document);
  // Backend messages already read "Failed to upload <name>: <reason>".
  const detail = failures.map((failure) => failure.message).join('; ');
  const succeeded = results.length - failures.length;

  return succeeded > 0
    ? `${succeeded} of ${results.length} files uploaded. ${detail}`
    : detail;
};

export const useUploadMultipleDocuments = () => {
  const queryClient = useQueryClient();

  return useApiMutation<
    DocumentUploadResponse[],
    BulkUploadError,
    {
      files: File[];
      collection_id?: string;
      metadata?: Record<string, unknown>;
      tags?: string[];
      entity_associations?: {
        entity_type: string;
        entity_id: string;
        role: string;
        context_type?: string;
        context_name?: string;
      }[];
    }
  >(
    async (data) => {
      const results = await documentService.uploadMultipleDocuments(data);

      if (results.some((result) => !result.document)) {
        const error = new APIError(
          buildBulkUploadErrorMessage(results),
          207,
          results
        ) as BulkUploadError;
        error.results = results;
        throw error;
      }

      return results;
    },
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Files uploaded successfully',
      // A partial failure still uploaded some files, so refresh either way.
      onError: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
      },
    }
  );
};

export const useDeleteDocument = () => {
  return useApiMutation<void, APIError, string>(
    (id) => documentService.deleteDocument(id),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Document deleted successfully',
    }
  );
};

// TODO: Implement move document functionality when backend support is added
// Will need to:
// 1. Add backend endpoint for moving documents
// 2. Add MoveDocumentRequest type to @/types/document
// 3. Add moveDocument method to documentService
// 4. Uncomment and implement this hook

export const useDownloadDocument = () => {
  return useApiMutation<void, APIError, { id: string; documentName: string }>(
    ({ id, documentName }) =>
      documentService.downloadDocument(id, documentName),
    {
      successMessage: 'Document downloaded successfully',
    }
  );
};

// Entity-specific hooks
export const useEntityCollections = (entityType: string, entityId: string) => {
  return useApiQuery(
    [...queryKeys.documents.all, 'entity', entityType, entityId, 'collections'],
    () => documentService.getEntityCollections(entityType, entityId),
    {
      enabled: !!entityType && !!entityId,
    }
  );
};

export const useEntityCollectionContents = (
  entityType: string,
  entityId: string,
  collectionId: string,
  options?: { enabled?: boolean }
) => {
  const isValidId =
    !!collectionId &&
    collectionId !== 'root' &&
    collectionId !== 'null' &&
    collectionId !== 'undefined';

  return useApiQuery(
    [
      ...queryKeys.documents.all,
      'entity',
      entityType,
      entityId,
      'collection',
      collectionId,
      'contents',
    ],
    () =>
      documentService.getEntityCollectionContents(
        entityType,
        entityId,
        collectionId
      ),
    {
      enabled:
        (options?.enabled ?? true) && !!entityType && !!entityId && isValidId,
    }
  );
};

export const useEntityFolder = (entityType: string, entityId: string) => {
  return useApiQuery(
    [...queryKeys.documents.all, 'entity', entityType, entityId, 'folder'],
    () => documentService.getEntityFolder(entityType, entityId),
    {
      enabled: !!entityType && !!entityId,
    }
  );
};

export const useCreateEntityCollection = () => {
  return useApiMutation<
    DocumentCollection,
    APIError,
    {
      entityType: EntityType;
      entityId: string;
      data: CreateCollectionRequest;
    }
  >(
    ({ entityType, entityId, data }) =>
      documentService.createEntityCollection(entityType, entityId, data),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Entity folder created successfully',
    }
  );
};

export const useSearchDocuments = (
  params: {
    query?: string;
    entity_type?: string;
    entity_id?: string;
    collection_id?: string;
    limit?: number;
  },
  options?: { enabled?: boolean }
) => {
  return useApiQuery(
    [...queryKeys.documents.all, 'search', params],
    () => documentService.searchDocuments(params),
    {
      enabled: options?.enabled ?? false,
    }
  );
};

// Hook for fetching presigned/tokenized download URL
export const useDownloadUrl = (
  documentId: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useApiQuery(
    [...queryKeys.documents.all, 'download-url', documentId],
    async () => {
      if (!documentId) throw new Error('Document ID is required');
      const response = await documentService.getDownloadUrl(documentId);
      return response;
    },
    {
      enabled: (options?.enabled ?? true) && !!documentId,
      staleTime: 30 * 1000, // Cache for 30 seconds (shorter than blob since URLs expire)
      gcTime: 60 * 1000, // Keep in cache for 1 minute
    }
  );
};

// Hook for fetching document as blob (for viewing) - fallback for local storage
export const useDocumentBlob = (
  documentId: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useApiQuery(
    [...queryKeys.documents.all, 'blob', documentId],
    async () => {
      if (!documentId) throw new Error('Document ID is required');
      const blob = await documentService.getDocumentBlob(documentId);
      return blob;
    },
    {
      enabled: (options?.enabled ?? true) && !!documentId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );
};

// Hook for updating document investor visibility
export const useUpdateDocumentInvestorVisibility = () => {
  return useApiMutation<
    void,
    APIError,
    {
      documentId: string;
      entityType: string;
      entityId: string;
      investorVisible: boolean;
    }
  >(
    ({ documentId, entityType, entityId, investorVisible }) =>
      documentService.updateDocumentInvestorVisibility(
        documentId,
        entityType,
        entityId,
        investorVisible
      ),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Document visibility updated successfully',
    }
  );
};

// Hook for bulk updating document investor visibility
export const useBulkUpdateInvestorVisibility = () => {
  return useApiMutation<
    { updated_count: number },
    APIError,
    {
      entityType: string;
      entityId: string;
      documentIds: string[];
      investorVisible: boolean;
    }
  >(
    ({ entityType, entityId, documentIds, investorVisible }) =>
      documentService.bulkUpdateInvestorVisibility(
        entityType,
        entityId,
        documentIds,
        investorVisible
      ),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Document visibility updated successfully',
    }
  );
};

export const useInvestorDocumentFolders = (investorId: string) => {
  return useApiQuery(
    queryKeys.documents.investorFundFolders(investorId),
    () => documentService.getInvestorDocumentFolders(investorId),
    { enabled: !!investorId }
  );
};

/** Uploads one file at a time through the investor-scoped endpoint, so the
 *  server writes the INVESTOR association and files it into the folder. */
export const useUploadInvestorDocuments = () => {
  return useApiMutation<
    void,
    APIError,
    {
      investorId: string;
      files: File[];
      collectionId: string;
      portalVisible: boolean;
      fundId: string | null;
      contextType: FundDocumentContextType | null;
    }
  >(
    async ({
      investorId,
      files,
      collectionId,
      portalVisible,
      fundId,
      contextType,
    }) => {
      for (const file of files) {
        await documentService.uploadEntityDocument(
          EntityType.INVESTOR,
          investorId,
          {
            file,
            collection_id: collectionId,
            portal_visible: portalVisible,
            fund_id: fundId ?? undefined,
            context_type: contextType ?? undefined,
          }
        );
      }
    },
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Documents uploaded',
    }
  );
};

export const useMoveInvestorDocument = () => {
  return useApiMutation<
    void,
    APIError,
    {
      investorId: string;
      documentId: string;
      fundId: string | null;
      contextType: FundDocumentContextType | null;
    }
  >(
    ({ investorId, documentId, fundId, contextType }) =>
      documentService.updateDocumentFundScope(
        documentId,
        EntityType.INVESTOR,
        investorId,
        fundId,
        contextType
      ),
    {
      invalidateKeys: [queryKeys.documents.all],
      successMessage: 'Document moved',
    }
  );
};
